/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description New Map
 */
import { Box } from '@chakra-ui/react'
import { supported } from '@mapbox/mapbox-gl-supported'
import polyline from '@mapbox/polyline'
import { useBodyColor } from '@zcode-ui/chakra-themes'
import { selectBoxParam } from '@zcode-ui/filters'
import { useMount, useRouteProps } from '@zcode-ui/hook'
import { StaticMap } from '@zcode-ui/static-map'
import { selectCity, selectGeo } from '@zcode-ui/web-zumper-global-state'
import { MAPBOX_BROWSER_KEY_Z4, MAPBOX_STYLE_KEY_Z4 } from '@zcode/nr/es/settings'
import deepclone from '@zcode/nr/es/shared/utils/deepclone'
import type { Geo } from '@zcode/state-type'
import 'mapbox-gl/dist/mapbox-gl.css'
import MapGL, {
  Layer,
  type MapProps as MapGLProps,
  type MapRef,
  Source,
} from '@vis.gl/react-mapbox'
import React, { type Ref, forwardRef } from 'react'
import { type GetProps, useSelector } from 'react-redux'
import { useMeasure } from 'react-use'

import { getData, lineLayer } from '../../helpers/interactiveLayerData'
import { selectIsCurrentSearchView } from '../../inspectors'
import { selectHoodName } from '../../module/location/selectors'

import * as styles from './style'

type FwdMapRef = Ref<MapRef> | undefined

interface MapProps extends MapGLProps {
  fallbackChildren?: React.ReactNode
  onFallback?: () => void
}

export const Map = forwardRef(
  ({ children, fallbackChildren, onFallback, ...props }: MapProps, mapRef: FwdMapRef) => {
    const { bgColor } = useBodyColor()
    const geo = useSelector(selectGeo)
    const city = useSelector(selectCity)

    const routeProps = useRouteProps()
    const isCurrentSearchView = selectIsCurrentSearchView(routeProps)

    const boxParam = selectBoxParam(routeProps)

    const cityDisplayBounds = city?.displayBounds
    const isHood = !!useSelector(selectHoodName)

    let data: GeoJSON.Feature | undefined
    const { hood } = geo
    if (hood?.encodedBounds && hood?.encodedBounds?.length && isHood) {
      const dataJson = polyline.toGeoJSON(hood?.encodedBounds?.[0]?.exterior)
      data = getData(dataJson, hood._data?.city, hood._data?.name)
    } else if (cityDisplayBounds) {
      const cityBounds = deepclone(cityDisplayBounds)
      data = getData(cityBounds, city?.name, undefined)
    } else {
      data = undefined
    }

    const mapStyle = MAPBOX_STYLE_KEY_Z4

    return (
      <Box<'div'> bg={bgColor} css={styles.mapContainer}>
        {typeof window === 'undefined' || supported() ? (
          <MapGL
            mapStyle={mapStyle}
            mapboxAccessToken={MAPBOX_BROWSER_KEY_Z4}
            ref={mapRef}
            reuseMaps
            style={{ width: '100%', height: '100%' }}
            {...props}
          >
            {(isHood || cityDisplayBounds) && !boxParam && isCurrentSearchView ? (
              <Source data={data} id="my-data" type="geojson">
                <Layer {...lineLayer} />
              </Source>
            ) : (
              <Source
                data={{ type: 'FeatureCollection', features: [] }}
                id="my-data"
                type="geojson"
              ></Source>
            )}

            {children}
          </MapGL>
        ) : typeof props.latitude === 'number' && typeof props.longitude === 'number' ? (
          <LegacyMap
            children={fallbackChildren}
            lat={props.latitude}
            lng={props.longitude}
            mapboxStyle={mapStyle.split('/')[4].split('?')[0]}
            mapboxToken={MAPBOX_BROWSER_KEY_Z4}
            mapboxUsername={mapStyle.split('/')[3]}
            onMount={onFallback}
          />
        ) : null}
      </Box>
    )
  }
)

type LegacyMapProps = Omit<GetProps<typeof StaticMap>, 'height' | 'width'> & {
  onMount?: () => void
}

const LegacyMap = ({ onMount, ...props }: LegacyMapProps) => {
  useMount(onMount)
  const [ref, { height, width }] = useMeasure<HTMLDivElement>()
  return (
    <Box<'div'> h="100%" ref={ref} w="100%">
      {width && height ? (
        <StaticMap height={Math.floor(height)} width={Math.floor(width)} {...props} />
      ) : null}
    </Box>
  )
}
